'use client';
// 电话输入框原始复合组件，但也是基于FormProvider的方式来处理表单的变化

import { Country } from '@/api/types/public';
import ActionSheet from '@/components/ActionSheet';
import FormInput, { InputProps } from '@/components/Form/FormInput';
import WapOnly from '@/components/Responsive/WapOnly';
import WebOnly from '@/components/Responsive/WebOnly';
import { env } from '@/utils/env';
import { formatPhoneNoByRule } from '@/utils/form-rule';
import { parsePhoneValue } from '@/utils/tools';
import { InputAdornment } from '@mui/material';
import { DownOutlined } from '@zeelool/icon';
import { omit } from 'lodash-es';
import React, { useEffect, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import Autocomplete from '../AutoComplete';

export type OriginalPhoneInputProps = Omit<InputProps, 'formatInput' | 'rules'> & {
  /**
   * 默认值, 可为以下格式:
   * 1. "US(+1)" - 仅国家代码
   * 2. "US(+1)1234567890" - 国家代码+电话号码
   */
  defaultValue?: string;

  value?: string;
  /**
   * 电话号码变化回调
   */
  onChange?: (telephone?: string) => void;
  /**
   * 国家列表
   */
  countryList: Country[];
  /**
   * 国家代码，支持外部输入
   */
  telCode?: string;
};

const OriginalPhoneInput: React.FC<OriginalPhoneInputProps> = ({
  id = '',
  defaultValue = env.NEXT_PUBLIC_LOCALE_DEFAULT_TELCODE,
  value,
  onChange,
  layout = 'inline',
  slotProps = {},
  name,
  control,
  countryList,
  telCode,
  ...restProps
}) => {
  // 解析默认值
  const { telCode: initialTelCode, telephone: initialTelephone } = parsePhoneValue(defaultValue);

  const form = useForm({
    defaultValues: {
      telephone: initialTelephone === '' ? undefined : initialTelephone,
      telCode: initialTelCode,
    },
  });

  const [showTelCodePopup, setShowTelCodePopup] = useState(false);

  // 当外部defaultValue变化时更新内部表单值
  useEffect(() => {
    if (defaultValue) {
      const { telCode: telCodeValue, telephone } = parsePhoneValue(defaultValue);
      form.setValue('telCode', telCodeValue);
      form.setValue('telephone', telephone);
    }
  }, [defaultValue, form]);

  // 当外部value变化时更新内部表单值
  useEffect(() => {
    if (value !== undefined && value !== null) {
      const { telCode: telCodeValue, telephone } = parsePhoneValue(value);
      form.setValue('telCode', telCodeValue);
      form.setValue('telephone', telephone);
    }
  }, [value, form]);

  const columns = countryList.map((item) => ({
    label: `${item.country_id}(+${item.tel_code})`,
    value: item,
  }));

  useEffect(() => {
    if (telCode) {
      form.setValue('telCode', telCode);
    }
  }, [telCode]);

  // 使用useWatch更高效地监听特定字段
  const innerTelCode = useWatch({
    control: form.control,
    name: 'telCode',
  });

  const telephone = useWatch({
    control: form.control,
    name: 'telephone',
  });

  // 电话号码格式化函数
  const formatPhoneInput = (_value: string): string => {
    const formTelCode = form.getValues('telCode');
    if (!_value) {
      return _value;
    }
    if (formTelCode === 'US(+1)') {
      return formatPhoneNoByRule(_value, '(###)###-####');
    } else {
      return _value.replace(/\D/g, '');
    }
  };

  useEffect(() => {
    const number = formatPhoneInput(telephone);
    form.setValue('telephone', number);
  }, [innerTelCode]);

  useEffect(() => {
    // 为了解决一开始默认就会触发校验的问题，通过telephone的值来判断是否需要触发校验
    if (innerTelCode && telephone !== undefined) {
      onChange(`${innerTelCode}${telephone}`);
    }
  }, [innerTelCode, telephone]);

  return (
    <>
      <WapOnly>
        <ActionSheet
          open={showTelCodePopup}
          onClose={() => {
            setShowTelCodePopup(false);
          }}
          title=""
          onSave={(country) => {
            form.setValue('telCode', `${country.country_id}(+${country.tel_code})`);

            setShowTelCodePopup(false);
          }}
          defaultValue={initialTelCode}
          options={columns}
        />
      </WapOnly>
      <FormProvider {...form}>
        <FormInput
          {...omit(restProps, ['value'])}
          id={id}
          name="telephone"
          control={form.control}
          layout={layout}
          formatInput={formatPhoneInput}
          useNativeError
          sx={{
            '& .MuiInputBase-root': {
              paddingRight: 0,
              paddingLeft: 0,
              '& .MuiInputAdornment-root': {
                margin: '0',
                height: '100%',
                maxHeight: 'none',
              },
            },
            '& .MuiOutlinedInput-input': {
              padding: '8px',
              height: '100%',
              boxSizing: 'border-box',
            },
            ...restProps.sx,
          }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment
                  position="start"
                  sx={{
                    '& .MuiInputLabel-root.Mui-error': {
                      color: 'red',
                    },
                  }}
                >
                  <WapOnly>
                    <div
                      className="flex items-center h-[48px]"
                      onClick={() => {
                        setShowTelCodePopup(true);
                      }}
                    >
                      <span className="text-primary-black font-br text-14 flex  w-[80px] items-center justify-center">
                        {innerTelCode}
                      </span>
                      <DownOutlined className="text-12 text-secondary-text" />
                    </div>
                  </WapOnly>
                  <WebOnly>
                    <Autocomplete
                      disableClearable
                      control={form.control}
                      name="telCode"
                      options={countryList.map((item) => `${item.country_id}(+${item.tel_code})`)}
                      formItemClassName=""
                      sx={{
                        '& .MuiAutocomplete-inputRoot': {
                          marginTop: '2px',
                        },
                        '& .MuiAutocomplete-inputRoot .MuiAutocomplete-input': {
                          minWidth: '108px',
                          textAlign: 'left',
                          paddingLeft: '16px',
                        },
                        '& .MuiAutocomplete-inputRoot .MuiAutocomplete-endAdornment': {
                          right: '0px',
                        },
                        '& .MuiFormControl-root': {
                          marginTop: '0px',
                        },
                        '& .MuiOutlinedInput-root': {
                          paddingLeft: '0px',
                          paddingRight: '8px !important', // 宽度太宽了
                        },
                        '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                          borderLeft: 0,
                          borderTop: 0,
                          borderBottom: 0,
                          borderRight: 0,
                          // paddingRight: 0,
                          paddingLeft: 0,
                          // borderRightColor: '#d4d4d4 !important',
                        },
                      }}
                    />
                  </WebOnly>
                </InputAdornment>
              ),
              ...slotProps.input,
            },
            htmlInput: {
              maxLength: innerTelCode === 'US(+1)' ? 13 : 15,
              style: { width: '100%' },
              ...slotProps.htmlInput,
            },
            ...slotProps,
          }}
        />
      </FormProvider>
    </>
  );
};

export default OriginalPhoneInput;
